import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId } from "react";
import { data, type Schemas } from "../api/client";
import { Page, Section, useApiQuery } from "../app/Page";
import { useBff, useCan, useMe } from "../app/session";
import { useT, type MessageId } from "../i18n";
import { ErrorState } from "../ui/states";
import { CopilotModel } from "./CopilotModel";
import { LimitsAndUsage } from "./Limits";
import { Members } from "./Members";
import { Tenants } from "./Tenants";

type Tenant = Schemas["TenantOut"];
type Policy = Tenant["evals_on_publish"];
type Mfa = Tenant["mfa"];
const POLICIES: Policy[] = ["off", "warn", "block"];
const MFA: Mfa[] = ["admins", "everyone", "off"];

export function SettingsPage() {
  const t = useT();
  const canMembers = useCan("members:manage");
  const canTenants = useCan("tenants:manage");
  const tenant = useApiQuery("tenant", (api) => api.GET("/v1/tenant"));
  const limits = useApiQuery("limits", (api) => api.GET("/v1/limits"));
  const members = useApiQuery("members", (api) => api.GET("/v1/members"), { enabled: canMembers });
  const kind = tenant.data?.kind;
  const children = useApiQuery("tenants", (api) => api.GET("/v1/tenants"), {
    enabled: canTenants && (kind === "reseller" || kind === "platform"),
  });
  return (
    <Page title={t("nav.settings")} description={t("settings.description")}>
      {canMembers && (
        <Section title={t("settings.members")} query={members}>
          {(list) => <Members members={list.data} />}
        </Section>
      )}
      <Section title={t("settings.security")} query={tenant}>
        {(current) => <Choice<Mfa> field="mfa" value={current.mfa} options={MFA} label={t("settings.mfa")}
          option={(m) => t(`settings.mfa.${m}` as MessageId)} hint={t("settings.mfaHint")} />}
      </Section>
      <Section title={t("settings.publishing")} query={tenant}>
        {(current) => <Choice<Policy> field="evals_on_publish" value={current.evals_on_publish} options={POLICIES}
          label={t("settings.evalsOnPublish")} option={(p) => t(`settings.evals.${p}` as MessageId)}
          hint={t("settings.evalsHint")} />}
      </Section>
      <Section title={t("settings.copilot")} query={tenant}>
        {(current) => <CopilotModel key={JSON.stringify(current.copilot ?? null)} current={current.copilot} />}
      </Section>
      <Section title={t("limits.title")} query={limits}>
        {(current) => <LimitsAndUsage current={current} />}
      </Section>
      {canTenants && (kind === "reseller" || kind === "platform") && (
        <Section title={t("tenants.title")} query={children}>
          {(list) => <Tenants tenants={list.data} platform={kind === "platform"} />}
        </Section>
      )}
    </Page>
  );
}

/** One of the tenant's own settings, an admin's to change (`settings:write`): saved as it is chosen. */
function Choice<V extends string>({ field, value, options, label, option, hint }: {
  field: "mfa" | "evals_on_publish";
  value: V;
  options: V[];
  label: string;
  option: (v: V) => string;
  hint: string;
}) {
  const t = useT();
  const id = useId();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const canChange = useCan("settings:write");
  const save = useMutation({
    mutationFn: (next: V) => data(api.PATCH("/v1/tenant", { body: { [field]: next } })),
    onSuccess: () => queries.invalidateQueries({ queryKey: ["tenant", tenant] }),
  });
  return (
    <div className="max-w-xl space-y-1">
      <label htmlFor={id} className="block text-sm font-medium">{label}</label>
      <select id={id} value={value} disabled={!canChange || save.isPending} aria-describedby={`${id}-hint`}
        onChange={(e) => save.mutate(e.target.value as V)}
        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm disabled:opacity-60">
        {options.map((o) => <option key={o} value={o}>{option(o)}</option>)}
      </select>
      <p id={`${id}-hint`} className="text-xs text-[var(--color-muted)]">{hint}</p>
      {save.isSuccess && <p role="status" className="text-sm">{t("settings.saved")}</p>}
      {save.isError && <ErrorState error={save.error} />}
    </div>
  );
}
